import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Switch,
  Tooltip,
  useTheme,
} from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import FormTextField from "../../../../../../views/components/common/form/FormTextField";
import ImageUpload from "../../../../../../views/components/common/upload/ImageUpload";
import { Swagpack } from "../../../../../../models/Swagstore/Packs";
import { GeneratedMockupApprovalStatus } from "../../../../../../models/MockupConfig/GeneratedMockup";
import type { GeneratedMockupStatus } from "../../../../../../models/MockupConfig/index";
// import { useStores } from "../../../../use-stores";
import leftIcon from "../../../../../../assets/left-svg.svg";
import rightIcon from "../../../../../../assets/right-svg.svg";
import sendIcon from "../../../../../../assets/Send.svg";
import commentsIcon from "../../../../../../assets/comments.svg";
import swageazyCubeIcon from "../../../../../../assets/swageazy-cube.svg";
import checkIcon from "../../../../../../assets/check.png";
import changesRequestedIcon from "../../../../../../assets/changes-requested.svg";
import pendingIcon from "../../../../../../assets/pending.svg";
import userIcon from "../../../../../../assets/user-icon.svg";
import AppDropDown from "../../../../../../views/components/common/filter/AppDropDown";
import {
  catalogSkuChoices,
  mockupMatchesCatalogSku,
  PRODUCT_LEVEL_SKU,
} from "./catalogSkuChoices";

interface Props {
  product: Swagpack | null;
  onBack: () => void;
  mockups: GeneratedMockupStatus[];
  comments: Array<{
    id: number;
    generated_mockup_id: number;
    comment: string;
    admin_id: number | null;
    user_id: number | null;
  }>;
  loadingComments: boolean;
  postingComment: boolean;
  approvingMockupId: number | null;
  rejectingMockupId: number | null;
  approvingOnBehalfMockupId: number | null;
  deletingMockupId: number | null;
  canRejectApprovedMockup: boolean;
  onFetchComments: (mockupId: number) => Promise<void>;
  onAddComment: (payload: { generated_mockup_id: number; comment: string }) => Promise<void>;
  onApproveMockup: (id: number) => Promise<void>;
  onRejectMockup: (payload: { id: number; rejection_reason: string; file?: File }) => Promise<void>;
  onApproveOnBehalfUser: (payload: { id: number; reason: string; file: File }) => Promise<void>;
  onShowOnStorefront: (id: number, showOnStorefront: boolean) => Promise<void>;
  updatingShowOnStorefrontId: number | null;
  onDeleteMockup: (id: number) => Promise<void>;
  onEditInGenerator: (payload: {
    mockupId: number;
    psdTemplateId?: number;
    variantSku?: string | null;
  }) => void | Promise<void>;
  onError: (title: string, message: string) => void;
}

const SwagstoreMockupManagerPage: React.FC<Props> = ({
  product,
  onBack,
  mockups: allMockups,
  comments,
  loadingComments,
  postingComment,
  approvingMockupId,
  rejectingMockupId,
  approvingOnBehalfMockupId,
  canRejectApprovedMockup,
  onFetchComments,
  onAddComment,
  onApproveMockup,
  onRejectMockup,
  onApproveOnBehalfUser,
  onShowOnStorefront,
  updatingShowOnStorefrontId,
  onEditInGenerator,
  onError,
}) => {
  const theme = useTheme();
  // const { userStore } = useStores();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [variantSku, setVariantSku] = useState(PRODUCT_LEVEL_SKU);
  const [comment, setComment] = useState("");
  const [onBehalfReason, setOnBehalfReason] = useState("");
  const [onBehalfFiles, setOnBehalfFiles] = useState<File[]>([]);
  const [openOnBehalfDialog, setOpenOnBehalfDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectFiles, setRejectFiles] = useState<File[]>([]);
  const [zoom, setZoom] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const thumbsRef = useRef<(HTMLDivElement | null)[]>([]);
  const chatRef = useRef<HTMLDivElement | null>(null);

  const skuChoices = useMemo(() => catalogSkuChoices(product), [product]);
  const mockups = useMemo(
    () => allMockups.filter((mockup) => mockupMatchesCatalogSku(mockup.variant_sku, variantSku)),
    [allMockups, variantSku],
  );

  useEffect(() => {
    setVariantSku(PRODUCT_LEVEL_SKU);
  }, [product?.id]);

  const selected = mockups.find((m) => m.id === selectedId) ?? mockups[0] ?? null;
  const idx = Math.max(0, mockups.findIndex((m) => m.id === (selected?.id ?? -1)));
  const status = selected?.approval_status ?? GeneratedMockupApprovalStatus.PENDING;
  const derivedFullyApproved =
    Boolean(selected?.approved_by_admin_id) &&
    (Boolean(selected?.approved_by_user_id) || Boolean(selected?.approved_on_behalf_by_admin_id)) &&
    status !== GeneratedMockupApprovalStatus.REJECTED;

  const isApproved = selected?.is_fully_approved ?? derivedFullyApproved;
  const isChangesRequested = status === GeneratedMockupApprovalStatus.CHANGES_REQUESTED;
  const isRejected = selected?.is_rejected ?? status === GeneratedMockupApprovalStatus.REJECTED;
  // const canDelete = selected?.can_delete ?? !isApproved;
  const canRejectSelected = Boolean(selected?.id) && !isRejected && (!isApproved || canRejectApprovedMockup);
  const isPending = !isApproved && !isChangesRequested && !isRejected;
  const allowActions = !isApproved && !isRejected;
  const hasAdminApproved = Boolean(selected?.approved_by_admin_id);
  const hasUserSideApproved =
    Boolean(selected?.approved_by_user_id) || Boolean(selected?.approved_on_behalf_by_admin_id);

  // const adminApprovalLabel = hasAdminApproved
  //   ? selected?.approved_by_admin_id === userStore.user?.id
  //     ? `Admin approved by - ${userStore.user?.name || "Current Admin"}`
  //     : `Admin approved by - Admin #${selected?.approved_by_admin_id}`
  //   : "";

  const formatDateTime = (value?: string | null) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  };

  const adminDisplayName =
    selected?.approved_by_admin_name || (selected?.approved_by_admin_id ? `Admin ${selected.approved_by_admin_id}` : "No");

  const userDisplayName =
    selected?.approved_by_user_name || (selected?.approved_by_user_id ? `User ${selected.approved_by_user_id}` : "No");

  const onBehalfDisplayName =
    selected?.approved_on_behalf_by_admin_name ||
    (selected?.approved_on_behalf_by_admin_id ? `Admin ${selected.approved_on_behalf_by_admin_id}` : "No");
  const rejectedByDisplayName =
    selected?.rejected_by_admin_name ||
    selected?.rejected_by_user_name ||
    (selected?.rejected_by_admin_id
      ? `Admin ${selected.rejected_by_admin_id}`
      : selected?.rejected_by_user_id
        ? `User ${selected.rejected_by_user_id}`
        : "No");

  const removeOnBehalfPreview = (image: { title?: string }) => {
    setOnBehalfFiles((prev) => {
      if (!prev.length) return prev;
      const index = prev.findIndex((file) => file.name === image?.title);
      if (index < 0) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeRejectPreview = (image: { title?: string }) => {
    setRejectFiles((prev) => {
      if (!prev.length) return prev;
      const index = prev.findIndex((file) => file.name === image?.title);
      if (index < 0) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const canSubmitOnBehalf =
    Boolean(selected?.id) &&
    Boolean(onBehalfReason.trim()) &&
    Boolean(onBehalfFiles?.[0]) &&
    approvingOnBehalfMockupId !== selected?.id;

  useEffect(() => {
    if (!mockups?.length) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => {
      if (prev != null && mockups.some((m) => m.id === prev)) {
        return prev;
      }
      return mockups[0]?.id ?? null;
    });
  }, [mockups]);

  useEffect(() => {
    if (selected?.id) void onFetchComments(selected.id);
  }, [selected?.id, onFetchComments]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }, 0);
    return () => clearTimeout(t);
  }, [comments]);

  return (
    <Box sx={{ p: { xs: 2, md: 2.5, xl: 3 }, pb: 4, maxWidth: { xs: "100%", lg: 1280, xl: 1360 }, mx: "auto" }}>
      <Stack direction="column" alignItems="flex-start" spacing={0.5} sx={{ mb: { xs: 1.5, lg: 2 } }}>
        <AppButton
          label="← Back"
          variant="transparentSecondary"
          onClick={onBack}
          fontSize="0.9rem"
          fontWeight={600}
          customStyles={{
            minHeight: "unset",
            minWidth: 0,
            px: 0,
            py: 0,
            justifyContent: "flex-start",
            textAlign: "left",
            backgroundColor: "transparent",
            border: "none",
            boxShadow: "none",
            color: theme.palette.text.secondary,
            "&:hover": {
              backgroundColor: "transparent",
              boxShadow: "none",
              color: theme.palette.text.primary,
            },
          }}
        />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ width: "100%", mt: 0.25 }}
        >
          <CustomTypography variant="heading4">{product?.label || "Product Mockups"}</CustomTypography>
          {skuChoices.length > 1 && (
            <Box sx={{ width: 220, flexShrink: 0 }}>
              <AppDropDown
                title="SKU"
                isMultiSelect={false}
                list={skuChoices}
                value={variantSku}
                onSingleSelectionChange={setVariantSku}
                placeholderText="All SKUs"
                width="220px"
                height="32px"
                fontSize="0.75rem"
              />
            </Box>
          )}
        </Stack>
      </Stack>

      <Grid2 container spacing={{ xs: 2, lg: 2.5 }}>
        <Grid2 size={{ xs: 12, lg: 7, xl: 8 }}>
          <Paper
            variant="outlined"
            sx={{
              background: theme.palette.customColors.lightGreyColor,
              borderRadius: "12px",
              mt: 2,
            }}
          >
            <Box
              ref={containerRef}
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                minHeight: { xs: "14rem", lg: "26rem", xl: "32rem" },
                maxHeight: { xs: "14rem", lg: "26rem", xl: "32rem" },
                p: { xs: 4, lg: 7, xl: 9 },
                cursor: "zoom-in",
              }}
              onMouseMove={(e) => {
                if (!containerRef.current) return;
                const target = e.target as HTMLElement | null;
                if (target?.closest('[data-disable-zoom="true"]')) {
                  if (zoom) setZoom(false);
                  return;
                }
                const r = containerRef.current.getBoundingClientRect();
                setZoomPos({
                  x: ((e.clientX - r.left) / r.width) * 100,
                  y: ((e.clientY - r.top) / r.height) * 100,
                });
              }}
              onMouseEnter={(e) => {
                const target = e.target as HTMLElement | null;
                if (target?.closest('[data-disable-zoom="true"]')) {
                  setZoom(false);
                  return;
                }
                setZoom(true);
              }}
              onMouseLeave={() => setZoom(false)}
            >
              {/* Status Icons */}
              {isApproved && (
                <IconButton data-disable-zoom="true" sx={{ position: "absolute", right: 15, top: 5 }}>
                  <img srcSet={checkIcon} alt="" />
                </IconButton>
              )}
              {isChangesRequested && (
                <IconButton data-disable-zoom="true" sx={{ position: "absolute", right: 1, top: 5 }}>
                  <img width="35" height="35" srcSet={changesRequestedIcon} alt="" />
                </IconButton>
              )}
              {isPending && (
                <IconButton data-disable-zoom="true" sx={{ position: "absolute", right: 1, top: 5 }}>
                  <img width="35" height="35" srcSet={pendingIcon} alt="Pending" />
                </IconButton>
              )}
              {isRejected && (
                <IconButton
                  data-disable-zoom="true"
                  sx={{ position: "absolute", right: 1, top: 5 }}
                >
                  <CloseIcon sx={{ color: theme.palette.error.main, width: 24, height: 24 }} />
                </IconButton>
              )}

              {/* Navigation Buttons */}
              {mockups.length > 1 && (
                <IconButton
                  data-disable-zoom="true"
                  sx={{ position: "absolute", left: 0 }}
                  onClick={() => {
                    const p = idx - 1 < 0 ? mockups.length - 1 : idx - 1;
                    setSelectedId(mockups[p]?.id ?? null);
                    thumbsRef.current[p]?.scrollIntoView({
                      behavior: "smooth",
                      inline: "center",
                      block: "nearest",
                    });
                  }}
                >
                  <img srcSet={leftIcon} alt="" />
                </IconButton>
              )}

              {selected ? (
                <img
                  src={selected.mockup_url}
                  alt="Product"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    minHeight: window.innerWidth >= 1200 ? "20rem" : "10rem",
                    maxHeight: window.innerWidth >= 1200 ? "20rem" : "10rem",
                    transition: "transform 0.3s ease-in-out",
                    transform: zoom ? "scale(2)" : "scale(1)",
                    transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`,
                  }}
                />
              ) : (
                <CustomTypography variant="heading5">NO MOCKUPS FOUND</CustomTypography>
              )}

              {mockups.length > 1 && (
                <IconButton
                  data-disable-zoom="true"
                  sx={{ position: "absolute", right: 0 }}
                  onClick={() => {
                    const n = idx + 1 >= mockups.length ? 0 : idx + 1;
                    setSelectedId(mockups[n]?.id ?? null);
                    thumbsRef.current[n]?.scrollIntoView({
                      behavior: "smooth",
                      inline: "center",
                      block: "nearest",
                    });
                  }}
                >
                  <img srcSet={rightIcon} alt="" />
                </IconButton>
              )}

              {selected && (
                <Box
                  data-disable-zoom="true"
                  sx={{
                    position: "absolute",
                    left: 12,
                    right: 12,
                    bottom: 14,
                    zIndex: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <Stack spacing={0.5}>
                        <CustomTypography variant="body2" color="#fff">
                          Status: {isRejected ? "Rejected" : isApproved ? "Approved" : "Pending"}
                        </CustomTypography>
                        <CustomTypography variant="body2" color="#fff">
                          Source: {selected?.source || "-"}
                        </CustomTypography>
                        <CustomTypography variant="body2" color="#fff">
                          Approved by admin: {adminDisplayName}
                        </CustomTypography>
                        <CustomTypography variant="body2" color="#fff">
                          Approved by user: {userDisplayName}
                        </CustomTypography>
                        <CustomTypography variant="body2" color="#fff">
                          Approved on behalf by: {onBehalfDisplayName}
                        </CustomTypography>
                        <CustomTypography variant="body2" color="#fff">
                          Created at: {formatDateTime(selected?.created_at)}
                        </CustomTypography>
                        <CustomTypography variant="body2" color="#fff">
                          Updated at: {formatDateTime(selected?.updated_at)}
                        </CustomTypography>
                        <CustomTypography variant="body2" color="#fff">
                          Iteration: {selected?.iteration_no ?? "-"}
                        </CustomTypography>
                        {selected?.approved_on_behalf_reason && (
                          <CustomTypography variant="body2" color="#fff">
                            On behalf reason: {selected.approved_on_behalf_reason}
                          </CustomTypography>
                        )}
                        {selected?.approved_on_behalf_evidence_url && (
                          <CustomTypography variant="body2" color="#fff">
                            Evidence URL: {selected.approved_on_behalf_evidence_url}
                          </CustomTypography>
                        )}
                        {selected?.rejection_reason && (
                          <CustomTypography variant="body2" color="#fff">
                            Rejection reason: {selected.rejection_reason}
                          </CustomTypography>
                        )}
                        {(selected?.rejected_by_admin_id || selected?.rejected_by_user_id) && (
                          <CustomTypography variant="body2" color="#fff">
                            Rejected by: {rejectedByDisplayName}
                          </CustomTypography>
                        )}
                        {selected?.rejected_at && (
                          <CustomTypography variant="body2" color="#fff">
                            Rejected at: {formatDateTime(selected.rejected_at)}
                          </CustomTypography>
                        )}
                        {selected?.rejection_evidence_url && (
                          <CustomTypography variant="body2" color="#fff">
                            Rejection evidence URL: {selected.rejection_evidence_url}
                          </CustomTypography>
                        )}
                      </Stack>
                    }
                  >
                    <IconButton
                      sx={{
                        backgroundColor: theme.palette.customColors.grayBackGroundColor,
                        width: 32,
                        height: 32,
                      }}
                    >
                      <InfoOutlinedIcon
                        sx={{ color: theme.palette.customColors.purpleMain, width: 25, height: 25 }}
                      />
                    </IconButton>
                  </Tooltip>

                  {!isRejected && (
                    <Stack direction="row" spacing={1}>
                      {!isApproved && (
                        <IconButton
                          sx={{
                            backgroundColor: theme.palette.customColors.lightPurple,
                            width: 32,
                            height: 32,
                          }}
                          onClick={() =>
                            selected?.id &&
                            onEditInGenerator({
                              mockupId: selected.id,
                              psdTemplateId: selected.psd_template_id,
                              variantSku: selected.variant_sku,
                            })
                          }
                          disabled={!selected}
                        >
                          <EditIcon sx={{ color: theme.palette.customColors.purpleMain, width: 18, height: 18 }} />
                        </IconButton>
                      )}

                      {!hasAdminApproved && (
                        <IconButton
                          sx={{
                            backgroundColor: theme.palette.customColors.lightBlue,
                            width: 32,
                            height: 32,
                          }}
                          onClick={() => selected?.id && void onApproveMockup(selected.id)}
                          disabled={!allowActions || approvingMockupId === selected.id}
                        >
                          <DoneAllIcon sx={{ color: theme.palette.customColors.purpleMain, width: 18, height: 18 }} />
                        </IconButton>
                      )}

                      {!hasUserSideApproved && (
                        <IconButton
                          sx={{
                            backgroundColor: theme.palette.customColors.lightPurple,
                            width: 32,
                            height: 32,
                          }}
                          onClick={() => {
                            setOnBehalfReason("");
                            setOnBehalfFiles([]);
                            setOpenOnBehalfDialog(true);
                          }}
                          disabled={!allowActions || approvingOnBehalfMockupId === selected.id}
                        >
                          <CheckIcon sx={{ color: theme.palette.success.main, width: 18, height: 18 }} />
                        </IconButton>
                      )}

                      {/* <IconButton
                        sx={{
                          backgroundColor: theme.palette.customColors.pinkLight,
                          width: 32,
                          height: 32,
                        }}
                        onClick={() => void onDeleteMockup(selected.id)}
                        disabled={!canDelete || deletingMockupId === selected.id}
                      >
                        <DeleteIcon sx={{ color: theme.palette.customColors.pinkDark, width: 18, height: 18 }} />
                      </IconButton> */}
                      {(!isApproved || canRejectApprovedMockup) && (
                        <IconButton
                          sx={{
                            backgroundColor: theme.palette.customColors.pinkLight,
                            width: 32,
                            height: 32,
                          }}
                          onClick={() => {
                            setRejectReason("");
                            setRejectFiles([]);
                            setOpenRejectDialog(true);
                          }}
                          disabled={!canRejectSelected || rejectingMockupId === selected?.id}
                        >
                          <CloseIcon sx={{ color: theme.palette.customColors.pinkDark, width: 18, height: 18 }} />
                        </IconButton>
                      )}
                    </Stack>
                  )}
                </Box>
              )}
            </Box>
          </Paper>

          {isApproved && selected?.id ? (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: 1.5,
                py: 1,
                borderRadius: "8px",
                border: "1px solid #D9D9E8",
                backgroundColor: "#fff",
              }}
            >
              <Box>
                <CustomTypography variant="body2" fontWeight={600}>
                  Show on store
                </CustomTypography>
                <CustomTypography variant="caption" color="text.secondary">
                  {selected.show_on_storefront === 0 || selected.show_on_storefront === false
                    ? "Hidden from the employee store. Approval stays."
                    : "Employees can see this approved mockup."}
                </CustomTypography>
              </Box>
              <Switch
                checked={
                  selected.show_on_storefront !== 0 &&
                  selected.show_on_storefront !== false
                }
                disabled={updatingShowOnStorefrontId === selected.id}
                onChange={(_, checked) => {
                  if (!selected?.id) return;
                  void onShowOnStorefront(selected.id, checked);
                }}
              />
            </Box>
          ) : null}

          <Box sx={{ display: "flex", gap: 2, mt: 2, overflowX: "auto", pb: 1, pr: 1 }}>
            {mockups.map((m, i) => (
              <Box
                key={m.id}
                sx={{
                  background:
                    selected?.id === m.id
                      ? theme.palette.customColors.lightPurple
                      : theme.palette.customColors.white,
                  borderRadius: "8px",
                  border: `2px solid ${selected?.id === m.id
                    ? theme.palette.customColors.purpleMain
                    : theme.palette.customColors.greyBorder
                    }`,
                }}
              >
                <Box
                  sx={{
                    minWidth: "5rem",
                    maxWidth: "5rem",
                    minHeight: "5rem",
                    maxHeight: "5rem",
                    overflow: "hidden",
                    cursor: "pointer",
                    p: 1,
                  }}
                  onClick={() => setSelectedId(m.id)}
                  ref={(el: HTMLDivElement | null) => (thumbsRef.current[i] = el)}
                >
                  <img
                    src={m.mockup_url}
                    alt={`Thumbnail ${i + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Grid2>

        <Grid2 size={{ xs: 12, lg: 5, xl: 4 }}>
          <Box
            sx={{
              borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
              display: "flex",
              alignItems: "center",
              pb: 1,
              mb: 2,
            }}
          >
            <IconButton>
              <img srcSet={commentsIcon} alt="" />
            </IconButton>
            <CustomTypography variant="heading6" fontWeight={600}>
              Comments
            </CustomTypography>
          </Box>

          <Box
            sx={{
              minHeight: { xs: "12rem", lg: "22rem", xl: "24rem" },
              maxHeight: { xs: "14rem", lg: "24rem", xl: "26rem" },
              overflowY: "auto",
              my: 1,
            }}
            ref={chatRef}
          >
            {loadingComments ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "10rem" }}>
                <CircularProgress color="inherit" />
              </Box>
            ) : comments.length > 0 ? (
              comments.map((c, i) => (
                <Box key={c.id || i} sx={{ mb: 1 }}>
                  {c.user_id ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar src={userIcon} alt="User Avatar" sx={{ width: 25, height: 25 }} />
                      <Box
                        sx={{
                          p: 1,
                          backgroundColor: theme.palette.customColors.grayBackGroundColor,
                          borderRadius: "0.5rem",
                          maxWidth: "fit-content",
                        }}
                      >
                        <CustomTypography variant="body2">{c.comment}</CustomTypography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          p: 1,
                          backgroundColor: theme.palette.customColors.lightBlue,
                          borderRadius: "0.5rem",
                          maxWidth: "fit-content",
                        }}
                      >
                        <CustomTypography variant="body2">{c.comment}</CustomTypography>
                      </Box>
                      <Avatar src={swageazyCubeIcon} alt="System Avatar" sx={{ width: 25, height: 25 }} />
                    </Box>
                  )}
                </Box>
              ))
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "10rem" }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  NO COMMENTS
                </CustomTypography>
              </Box>
            )}
          </Box>

          {allowActions && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 1,
                gap: 1,
                borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
                pt: 1,
              }}
            >
              <TextField
                fullWidth
                placeholder="Type your message..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (!selected?.id || !comment.trim()) return;
                    void onAddComment({ generated_mockup_id: selected.id, comment: comment.trim() }).then(() =>
                      setComment("")
                    );
                  }
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          color="primary"
                          disabled={!selected || !comment.trim() || postingComment}
                          onClick={() => {
                            if (!selected?.id || !comment.trim()) return;
                            void onAddComment({ generated_mockup_id: selected.id, comment: comment.trim() }).then(() =>
                              setComment("")
                            );
                          }}
                        >
                          <img srcSet={sendIcon} alt="" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
          )}
        </Grid2>
      </Grid2>

      {/* Approve On Behalf Dialog */}
      <Dialog
        open={openRejectDialog}
        onClose={() => {
          setOpenRejectDialog(false);
          setRejectReason("");
          setRejectFiles([]);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject Mockup</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <FormTextField
              title="Reason"
              placeholder="Add reason"
              multiline={true}
              rows={3}
              value={rejectReason}
              onChange={(_, value) => setRejectReason(value ?? "")}
              sx={{ mb: 0 }}
            />
            <ImageUpload
              id={`reject-${String(product?.id || "")}`}
              size="large"
              previewImage
              showImagesList={false}
              multiple={false}
              existingImageList={[]}
              minWidth="100%"
              newImageList={rejectFiles}
              disabled={!selected || rejectingMockupId === selected?.id}
              addImagesToBeUploaded={(files: File[]) => setRejectFiles(files ?? [])}
              deleteImage={removeRejectPreview as any}
              removeImage={removeRejectPreview as any}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <AppButton
            label="Cancel"
            variant="secondary"
            onClick={() => {
              setOpenRejectDialog(false);
              setRejectReason("");
              setRejectFiles([]);
            }}
          />
          <AppButton
            label="Confirm Reject"
            variant="primary"
            onClick={() => {
              if (!selected?.id) return;
              if (!rejectReason.trim()) {
                onError("Reason required", "Please add rejection reason.");
                return;
              }
              if (isApproved && !rejectFiles?.[0]) {
                onError("Evidence required", "Please upload evidence image.");
                return;
              }
              void onRejectMockup({
                id: selected.id,
                rejection_reason: rejectReason.trim(),
                file: rejectFiles?.[0],
              }).then(() => {
                setOpenRejectDialog(false);
                setRejectReason("");
                setRejectFiles([]);
              });
            }}
            disabled={
              !selected ||
              !rejectReason.trim() ||
              (isApproved && !rejectFiles?.[0]) ||
              rejectingMockupId === selected?.id
            }
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={openOnBehalfDialog}
        onClose={() => {
          setOpenOnBehalfDialog(false);
          setOnBehalfReason("");
          setOnBehalfFiles([]);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Approve on Behalf of User</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <FormTextField
              title="Reason"
              placeholder="Add reason"
              multiline={true}
              rows={3}
              value={onBehalfReason}
              onChange={(_, value) => setOnBehalfReason(value ?? "")}
              sx={{ mb: 0 }}
            />
            <ImageUpload
              id={`onbehalf-${String(product?.id || "")}`}
              size="large"
              previewImage
              showImagesList={false}
              multiple={false}
              existingImageList={[]}
              minWidth="100%"
              newImageList={onBehalfFiles}
              disabled={!selected || approvingOnBehalfMockupId === selected.id}
              addImagesToBeUploaded={(files: File[]) => setOnBehalfFiles(files ?? [])}
              deleteImage={removeOnBehalfPreview as any}
              removeImage={removeOnBehalfPreview as any}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <AppButton
            label="Cancel"
            variant="secondary"
            onClick={() => {
              setOpenOnBehalfDialog(false);
              setOnBehalfReason("");
              setOnBehalfFiles([]);
            }}
          />
          <AppButton
            label="Confirm"
            variant="primary"
            onClick={() => {
              if (!selected?.id) return;
              if (!onBehalfReason.trim()) {
                onError("Reason required", "Please add approve-on-behalf reason.");
                return;
              }
              if (!onBehalfFiles?.[0]) {
                onError("Evidence required", "Please upload evidence image.");
                return;
              }
              void onApproveOnBehalfUser({
                id: selected.id,
                reason: onBehalfReason.trim(),
                file: onBehalfFiles[0],
              }).then(() => {
                setOpenOnBehalfDialog(false);
                setOnBehalfReason("");
                setOnBehalfFiles([]);
              });
            }}
            disabled={!canSubmitOnBehalf}
          />
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default SwagstoreMockupManagerPage;